import {
  MinorAddressFormType,
  MinorMailingFormFields,
} from '@/constants/minor';

import { AddressForm } from '@/types/form';
import { CountryISO } from '@/types/places';
import { ResidenceType, User } from '@/types/user';

import {
  getInitialMailingSameAsResidential,
  getMailingAddressFromUser,
  getPermanentAddressFromUser,
  isAddressDetailPopulated,
} from '@/utils/address';
import {
  getNormalizedTaxResidences,
  getOtherTaxCountries,
  getOtherTins,
  isTrinidadOrTobagoUser,
  shouldShowTinForCountry,
} from '@/utils/user';

export type MinorAddressData = {
  childLivesWithPersonName?: string;
  childLivesWithRelationship?: string;
  childLivesWithRelationshipDescription?: string;
  line1: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  residence?: ResidenceType;
};

export type MinorTaxData = {
  differentTaxResidency: boolean;
  taxResidences: string[];
  usTin: string;
  localTin: string;
  otherTins: Record<string, string>;
};

export const getPermanentAddressSource = getPermanentAddressFromUser;
export const getMailingAddressSource = getMailingAddressFromUser;

export function hasMailingAddress(user?: User): boolean {
  return isAddressDetailPopulated(getMailingAddressSource(user));
}

export const getMailingFormDefaults = (user?: User): MinorMailingFormFields => {
  const mailing = getMailingAddressSource(user);
  return {
    mailingSameAsResidential: user
      ? getInitialMailingSameAsResidential(user)
      : true,
    mailingLine1: mailing?.line1 ?? '',
    mailingCity: mailing?.city ?? '',
    mailingState: mailing?.state ?? '',
    mailingZip: mailing?.zip ?? '',
    mailingCountry: mailing?.country ?? '',
    residenceCountry: user?.residenceCountry ?? '',
  };
};

export const getMinorAddressData = (minorUser?: User): MinorAddressData => {
  const permanent = getPermanentAddressSource(minorUser);
  return {
    line1: permanent?.line1 || '',
    city: permanent?.city || '',
    state: permanent?.state || '',
    zip: permanent?.zip || '',
    country: permanent?.country || '',
    residence: permanent?.residence ?? minorUser?.addressDetailed?.residence,
    childLivesWithPersonName: permanent?.childLivesWithPersonName,
    childLivesWithRelationship: permanent?.childLivesWithRelationship,
    childLivesWithRelationshipDescription:
      permanent?.childLivesWithRelationshipDescription,
  };
};

export const getAdultAddressData = (adultUser?: User): MinorAddressData => {
  const permanent = getPermanentAddressSource(adultUser);
  return {
    line1: permanent?.line1 || '',
    city: permanent?.city || '',
    state: permanent?.state || '',
    zip: permanent?.zip || '',
    country: permanent?.country || '',
    residence: permanent?.residence ?? adultUser?.addressDetailed?.residence,
  };
};

export function minorAddressFormToAddressFormForMailing(
  form: MinorAddressFormType,
): AddressForm {
  return {
    line1: form.line1,
    city: form.city,
    state: form.state,
    zip: form.zip,
    country: form.country,
    residence: form.residence ?? ('' as AddressForm['residence']),
    differentTaxResidency: form.differentTaxResidency,
    mailingSameAsResidential: form.mailingSameAsResidential ?? true,
    mailingLine1: form.mailingLine1 ?? '',
    mailingCity: form.mailingCity ?? '',
    mailingState: form.mailingState ?? '',
    mailingZip: form.mailingZip ?? '',
    mailingCountry: form.mailingCountry ?? '',
    residenceCountry: form.residenceCountry,
  };
}

export const getMinorTaxData = (
  minorUser: User | undefined,
  normalizedTaxResidences: string[],
  isDifferentTaxResidency: boolean,
  requiredLocalTIN: boolean,
  localISO: string,
): MinorTaxData => {
  const otherTins = getOtherTins(minorUser?.tin, [CountryISO.US, localISO]);
  return {
    differentTaxResidency: isDifferentTaxResidency,
    taxResidences: normalizedTaxResidences,
    usTin: minorUser?.tin?.[CountryISO.US] || '',
    localTin: requiredLocalTIN ? minorUser?.tin?.[localISO] || '' : '',
    otherTins,
  };
};

export const calculateIsDifferentTaxResidency = (
  normalizedTaxResidences: string[],
  minorAddressCountry?: string,
): boolean => {
  if (normalizedTaxResidences.length > 1) {
    return true;
  }

  if (normalizedTaxResidences.length === 1) {
    return normalizedTaxResidences[0] !== minorAddressCountry;
  }

  return false;
};

export const getInitialShowDocumentUpload = (minorUser?: User): boolean => {
  if (!minorUser?.addressDetailed?.line1) return false;
  if (minorUser?.addressDetailed?.sameAsAdult === true) return false;
  return true;
};

type TinVisibilityParams = {
  effectiveDifferentTaxResidency: boolean;
  taxResidences: string[];
  country: string;
  requiredLocalTIN: boolean;
  localISO: string;
  minorUser?: User;
};

export type TinVisibilityResult = {
  shouldShowUsTin: boolean;
  shouldShowLocalTin: boolean;
  otherCountriesTin: string[];
};

export const calculateTinVisibility = ({
  effectiveDifferentTaxResidency,
  taxResidences,
  country,
  requiredLocalTIN,
  localISO,
  minorUser,
}: TinVisibilityParams): TinVisibilityResult => {
  const selectedTaxCountries = (
    effectiveDifferentTaxResidency ? taxResidences : [country]
  ).filter(Boolean);

  const shouldShowLocalTin =
    requiredLocalTIN &&
    shouldShowTinForCountry(
      minorUser,
      selectedTaxCountries,
      localISO as CountryISO,
    );

  return {
    shouldShowUsTin: shouldShowTinForCountry(
      minorUser,
      selectedTaxCountries,
      CountryISO.US,
    ),
    shouldShowLocalTin,
    otherCountriesTin: shouldShowLocalTin
      ? getOtherTaxCountries(selectedTaxCountries, [CountryISO.US, localISO])
      : getOtherTaxCountries(selectedTaxCountries, [CountryISO.US]),
  };
};

type BuildTinPayloadParams = {
  data: MinorAddressFormType;
  shouldShowUsTin: boolean;
  shouldShowLocalTin: boolean;
  otherCountriesTin: string[];
  localISO: string;
};

export const buildTinPayload = ({
  data,
  shouldShowUsTin,
  shouldShowLocalTin,
  otherCountriesTin,
  localISO,
}: BuildTinPayloadParams): Record<string, string> => {
  const tin: Record<string, string> = {};

  if (shouldShowUsTin && data.usTin) {
    tin[CountryISO.US] = data.usTin;
  }
  if (shouldShowLocalTin && data.localTin) {
    tin[localISO] = data.localTin;
  }
  if (otherCountriesTin?.length && data.otherTins) {
    otherCountriesTin.forEach(countryCode => {
      if (data.otherTins[countryCode]) {
        tin[countryCode] = data.otherTins[countryCode];
      }
    });
  }

  return tin;
};

type GetMinorInitialCountryArgs = {
  adultUser?: User;
  minorUser?: User;
  minorAddress: MinorAddressData;
  localISO: string;
};

export const getMinorInitialCountry = ({
  adultUser,
  minorUser,
  minorAddress,
  localISO,
}: GetMinorInitialCountryArgs) => {
  const isTTUser = isTrinidadOrTobagoUser(adultUser);
  const minorCitizenship = minorUser?.citizenship;
  const isMinorCitizenshipTT = [minorUser?.citizenship, minorUser?.citizenship2]
    .filter(Boolean)
    .map(code => (code as string).toUpperCase())
    .includes(CountryISO.Trinidad);
  if (isTTUser && minorCitizenship) {
    if (isMinorCitizenshipTT) {
      return CountryISO.Trinidad;
    }
    return minorCitizenship;
  }
  return minorAddress.country || localISO;
};

export { getNormalizedTaxResidences };
